import { useI18n } from "@wato/i18n/react";
import { WhatsAppBubble } from "./WhatsAppBubble";

export interface ChatPreviewMessage {
  readonly from: "customer" | "business";
  readonly text: string;
  /** Quick-reply / call-to-action button labels shown under a business message. */
  readonly buttons?: readonly string[];
  /** Display time, e.g. "10:02". Purely illustrative. */
  readonly time?: string;
}

/** A day/date separator row between messages (e.g. "Day 2", "Monday"). Label is already localized. */
export interface ChatPreviewDay {
  readonly kind: "day";
  readonly label: string;
}

export type ChatPreviewItem = ChatPreviewMessage | ChatPreviewDay;

export interface ChatPreviewProps {
  readonly messages: readonly ChatPreviewItem[];
  readonly businessName: string;
  /** One line under the phone explaining what the example shows. */
  readonly caption: string;
  readonly testId?: string;
  /** Language of the example messages; sets direction (RTL for Arabic). Defaults to the locale. */
  readonly lang?: "ar" | "en";
}

/**
 * Phone-frame mockup of an EXPECTED conversation (never live data), so a merchant sees what
 * their customer will receive before building anything. Direction follows the active locale;
 * bubbles are aligned with logical utilities (business = inline-end, customer = inline-start).
 * A visible "illustrative example" badge is always rendered.
 */
export function ChatPreview({
  messages,
  businessName,
  caption,
  testId = "chat-preview",
  lang,
}: ChatPreviewProps) {
  const { t, dir: localeDir } = useI18n();
  const dir = lang ? (lang === "ar" ? "rtl" : "ltr") : localeDir;
  const initial = Array.from(businessName.trim())[0] ?? "";
  return (
    <figure
      dir={dir}
      data-testid={testId}
      data-chat-preview=""
      className="m-0 flex w-full max-w-xs flex-col gap-2"
    >
      <div className="overflow-hidden rounded-3xl border-4 border-line-strong bg-surface-muted shadow-sm">
        <div className="flex items-center gap-2 border-b border-line bg-surface px-3 py-2">
          <span
            aria-hidden="true"
            className="flex size-8 shrink-0 items-center justify-center rounded-full bg-action-ghost-hover text-sm font-semibold text-on-action-ghost"
          >
            {initial}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">
            {businessName}
          </span>
          <span
            data-testid="chat-preview-badge"
            className="shrink-0 rounded-full border border-warning-icon bg-warning-bg px-2 py-0.5 text-xs font-medium text-warning-fg"
          >
            {t("chatPreview.badge")}
          </span>
        </div>
        <ul
          aria-label={t("chatPreview.conversation")}
          className="m-0 flex min-h-48 list-none flex-col gap-2 p-3"
        >
          {messages.map((m, i) => {
            if ("kind" in m) {
              return (
                <li
                  key={i}
                  data-kind="day"
                  data-testid="chat-preview-day"
                  className="self-center rounded-full bg-surface px-3 py-0.5 text-xs text-fg-muted shadow-sm"
                >
                  {m.label}
                </li>
              );
            }
            const business = m.from === "business";
            return (
              <li
                key={i}
                data-from={m.from}
                className={`flex max-w-[85%] flex-col gap-0.5 ${business ? "self-end" : "self-start"}`}
              >
                <span className="sr-only">
                  {business ? businessName : t("chatPreview.customer")}:
                </span>
                <WhatsAppBubble
                  direction={dir}
                  body={m.text}
                  tone={business ? "outgoing" : "neutral"}
                  testId="chat-preview-bubble"
                  {...(m.buttons && m.buttons.length > 0
                    ? { buttons: m.buttons.map((text) => ({ text })) }
                    : {})}
                />
                {m.time ? (
                  <span className="px-1 text-xs text-fg-subtle" data-testid="chat-preview-time">
                    {m.time}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
      <figcaption className="text-xs text-fg-muted">{caption}</figcaption>
    </figure>
  );
}
